import { getLeadContactTier, isContactResolved } from '@/lib/contact-vault/lead-contact-readiness';
import { normalizeEmail } from '@/lib/contact-vault/normalize';
import { belongsOnOperatorsNotBrowse } from '@/lib/leads/operator-umbrella';
import { getOutreachSafety, type OutreachSafetyInput } from '@/lib/leads/outreach-safety';
import type { ContactVaultEmailTier } from '@/types/contact-vault';
import type { LeadOutreachEvent } from '@/types/lead-outreach';
import type { Lead, PriorityBand } from '@/types/leads';

export const WEEK_QUEUE_TARGET_SIZE = 40;
export const WEEK_QUEUE_NAMED_CONTACTS_ONLY = true;
export const WEEK_QUEUE_RECENT_CONTACT_COOLDOWN_DAYS = 30;

const terminalStatuses = new Set(['disqualified', 'suppressed', 'do_not_contact', 'converted']);
const outreachTouchEventTypes = new Set<LeadOutreachEvent['event_type']>(['sent', 'follow_up_sent']);

const bandBonus: Record<PriorityBand, number> = {
  priority: 12,
  review: 8,
  recheck: 4,
  hold: 0,
  suppressed: -100,
};

const contactTierBonus = {
  named: 15,
  role: 10,
  generic: 4,
  unknown: 0,
} as const;

export function wasRecentlyContacted(
  events: LeadOutreachEvent[],
  withinDays = WEEK_QUEUE_RECENT_CONTACT_COOLDOWN_DAYS,
  now = Date.now()
): boolean {
  const cutoff = now - withinDays * 24 * 60 * 60 * 1000;
  return events.some((event) => {
    if (!outreachTouchEventTypes.has(event.event_type)) return false;
    const occurredAt = new Date(event.occurred_at).getTime();
    return !Number.isNaN(occurredAt) && occurredAt >= cutoff;
  });
}

export interface WeekQueueVaultMatch {
  confidence: 'high' | 'medium' | 'low';
  email_tier: ContactVaultEmailTier;
  email: string | null;
  contact_person: string | null;
}

export interface WeekQueueEligibilityBreakdown {
  candidates: number;
  eligible: number;
  namedOnRecord: number;
  vaultNamed: number;
  notNamed: number;
  recentlyContacted: number;
  repliedOrOptedOut: number;
  safetyBlocked: number;
  safetyNeedsReview: number;
  onNurtureTrack: number;
  existingLeadDuplicate: number;
  onOwnerOperatorTrack: number;
  /** Eligible leads dropped because another queued card already owns this email. */
  duplicateEmailCollapsed: number;
  missingEmail: number;
  other: number;
}

export function emptyWeekQueueEligibilityBreakdown(
  candidates = 0
): WeekQueueEligibilityBreakdown {
  return {
    candidates,
    eligible: 0,
    namedOnRecord: 0,
    vaultNamed: 0,
    notNamed: 0,
    recentlyContacted: 0,
    repliedOrOptedOut: 0,
    safetyBlocked: 0,
    safetyNeedsReview: 0,
    onNurtureTrack: 0,
    existingLeadDuplicate: 0,
    onOwnerOperatorTrack: 0,
    duplicateEmailCollapsed: 0,
    missingEmail: 0,
    other: 0,
  };
}

/** Effective outreach email for week-queue matching (record first, then vault). */
export function weekQueueLeadEmail(
  lead: { email?: string | null },
  vaultMatch?: WeekQueueVaultMatch | null
): string | null {
  return normalizeEmail(lead.email) ?? normalizeEmail(vaultMatch?.email);
}

/**
 * Track B nurture deals own these emails — exclude from Track A week queue.
 * New leadgen leads still queue unless their email is already on a nurture deal.
 */
export function isOnNurtureTrack(
  lead: { email?: string | null },
  nurtureEmails: Set<string>,
  vaultMatch?: WeekQueueVaultMatch | null
): boolean {
  if (nurtureEmails.size === 0) return false;
  const email = weekQueueLeadEmail(lead, vaultMatch);
  return Boolean(email && nurtureEmails.has(email));
}

/** Duplicate of another prospecting lead — not a Track A cold target. */
export function isExistingLeadDuplicate(
  lead: Pick<WeekQueueRankingLead, 'matched_entity_type'>
): boolean {
  return lead.matched_entity_type === 'existing_lead';
}

/** Cold Prospecting should not show nurture-owned, duplicate, or operator-umbrella stores. */
export function shouldHideFromColdProspecting(
  lead: Pick<WeekQueueRankingLead, 'id' | 'matched_entity_type' | 'operator_id'> & {
    email?: string | null;
  },
  options: {
    nurtureLeadIds?: Set<string>;
    nurtureEmails?: Set<string>;
    ownerOperatorIds?: Set<string>;
    vaultMatch?: WeekQueueVaultMatch | null;
    /** Default true. Set false so search / follow-up / sent views can still open operator stores. */
    hideOperatorUmbrellas?: boolean;
  } = {}
): boolean {
  if (isExistingLeadDuplicate(lead)) return true;
  if (options.nurtureLeadIds?.has(lead.id)) return true;
  if (options.nurtureEmails && isOnNurtureTrack(lead, options.nurtureEmails, options.vaultMatch)) {
    return true;
  }
  // Multi-store / chain / linked umbrella cards belong on Operators, not default Browse.
  if (options.hideOperatorUmbrellas !== false && belongsOnOperatorsNotBrowse(lead)) return true;
  return false;
}

export interface WeekQueueRankedLead {
  leadId: string;
  score: number;
  reasons: string[];
  contactSource: 'record' | 'vault';
}

export interface WeekQueueRankResult {
  ranked: WeekQueueRankedLead[];
  breakdown: WeekQueueEligibilityBreakdown;
  shortfall: number;
}

export type WeekQueueRankingLead = Pick<
  Lead,
  | 'id'
  | 'business_name'
  | 'dba'
  | 'city'
  | 'state'
  | 'email'
  | 'contact_person'
  | 'contact_resolved_at'
  | 'lead_score'
  | 'need_score'
  | 'winnability_score'
  | 'value_score'
  | 'priority_band'
  | 'solvable_pain_points'
  | 'matched_proof_metric_ids'
  | 'status'
  | 'do_not_contact'
  | 'match_status'
  | 'suppression_reason'
  | 'matched_entity_type'
  | 'matched_entity_id'
  | 'operator_id'
  | 'segment'
  | 'location_count'
  | 'contact_readiness_status'
  | 'operator_link_method'
> &
  OutreachSafetyInput;

export function getEffectiveContactTier(
  lead: Pick<Lead, 'email' | 'contact_person'>,
  vaultMatch?: WeekQueueVaultMatch | null
): ContactVaultEmailTier {
  const recordTier = getLeadContactTier(lead);
  if (recordTier === 'named') return 'named';
  if (
    vaultMatch?.confidence === 'high' &&
    vaultMatch.email_tier === 'named' &&
    vaultMatch.email?.trim()
  ) {
    return 'named';
  }
  return recordTier;
}

function hasNamedContact(
  lead: Pick<Lead, 'email' | 'contact_person' | 'contact_resolved_at'>,
  _vaultMatch?: WeekQueueVaultMatch | null
): boolean {
  if (isContactResolved(lead) && lead.email?.trim()) return true;
  return Boolean(lead.contact_person?.trim() && lead.email?.trim() && getLeadContactTier(lead) === 'named');
}

export function isEligibleForWeekQueue(
  lead: WeekQueueRankingLead,
  events: LeadOutreachEvent[] = [],
  vaultMatch?: WeekQueueVaultMatch | null,
  nurtureEmails: Set<string> = new Set(),
  _ownerOperatorIds: Set<string> = new Set()
): boolean {
  if (!lead.email?.trim() || !lead.contact_person?.trim()) return false;
  if (lead.contact_readiness_status !== 'ready') return false;
  if (lead.do_not_contact) return false;
  if (lead.status && terminalStatuses.has(lead.status)) return false;
  if (lead.priority_band === 'suppressed') return false;
  if (isOnNurtureTrack(lead, nurtureEmails, vaultMatch)) return false;
  if (isExistingLeadDuplicate(lead)) return false;
  if (belongsOnOperatorsNotBrowse(lead)) return false;
  if (WEEK_QUEUE_NAMED_CONTACTS_ONLY && !hasNamedContact(lead, vaultMatch)) return false;
  if (events.some((event) => event.event_type === 'opted_out')) return false;
  if (events.some((event) => event.event_type === 'replied')) return false;
  if (wasRecentlyContacted(events)) return false;
  return getOutreachSafety(lead).state === 'ready';
}

export function summarizeWeekQueueEligibility(
  leads: WeekQueueRankingLead[],
  eventsByLeadId: Map<string, LeadOutreachEvent[]>,
  vaultMatchesByLeadId: Map<string, WeekQueueVaultMatch | null> = new Map(),
  nurtureEmails: Set<string> = new Set(),
  _ownerOperatorIds: Set<string> = new Set()
): WeekQueueEligibilityBreakdown {
  const breakdown = emptyWeekQueueEligibilityBreakdown(leads.length);

  for (const lead of leads) {
    const events = eventsByLeadId.get(lead.id) ?? [];
    const vaultMatch = vaultMatchesByLeadId.get(lead.id) ?? null;

    if (!lead.email?.trim()) {
      breakdown.missingEmail += 1;
      continue;
    }
    if (
      lead.do_not_contact ||
      (lead.status && terminalStatuses.has(lead.status)) ||
      lead.priority_band === 'suppressed'
    ) {
      breakdown.other += 1;
      continue;
    }
    if (isOnNurtureTrack(lead, nurtureEmails, vaultMatch)) {
      breakdown.onNurtureTrack += 1;
      continue;
    }
    if (isExistingLeadDuplicate(lead)) {
      breakdown.existingLeadDuplicate += 1;
      continue;
    }
    if (belongsOnOperatorsNotBrowse(lead)) {
      breakdown.onOwnerOperatorTrack += 1;
      continue;
    }
    if (events.some((event) => event.event_type === 'opted_out' || event.event_type === 'replied')) {
      breakdown.repliedOrOptedOut += 1;
      continue;
    }
    if (wasRecentlyContacted(events)) {
      breakdown.recentlyContacted += 1;
      continue;
    }
    const safety = getOutreachSafety(lead);
    if (safety.state === 'blocked') {
      breakdown.safetyBlocked += 1;
      continue;
    }
    if (safety.state === 'needs_review' || safety.state === 'account_expansion_review') {
      breakdown.safetyNeedsReview += 1;
      continue;
    }
    if (safety.state !== 'ready') {
      breakdown.other += 1;
      continue;
    }

    const recordNamed = getLeadContactTier(lead) === 'named';
    const vaultNamed = false;
    if (WEEK_QUEUE_NAMED_CONTACTS_ONLY && !recordNamed && !vaultNamed) {
      breakdown.notNamed += 1;
      continue;
    }

    breakdown.eligible += 1;
    if (recordNamed) breakdown.namedOnRecord += 1;
    else if (vaultNamed) breakdown.vaultNamed += 1;
  }

  return breakdown;
}

export function formatWeekQueueRebuildSummary(
  added: number,
  result: Pick<WeekQueueRankResult, 'breakdown' | 'shortfall'>
): string {
  const parts = [`${added} of ${WEEK_QUEUE_TARGET_SIZE} queued`];
  if (result.breakdown.eligible > 0) {
    parts.push(`${result.breakdown.eligible} eligible`);
  }
  if (result.breakdown.vaultNamed > 0) {
    parts.push(`${result.breakdown.vaultNamed} via vault`);
  }
  if (result.shortfall > 0) {
    const blockers: string[] = [];
    if (result.breakdown.notNamed > 0) blockers.push(`${result.breakdown.notNamed} not named`);
    if (result.breakdown.recentlyContacted > 0) {
      blockers.push(`${result.breakdown.recentlyContacted} contacted <30d`);
    }
    if (result.breakdown.onNurtureTrack > 0) {
      blockers.push(`${result.breakdown.onNurtureTrack} on nurture`);
    }
    if (result.breakdown.onOwnerOperatorTrack > 0) {
      blockers.push(`${result.breakdown.onOwnerOperatorTrack} owner umbrella`);
    }
    if (result.breakdown.existingLeadDuplicate > 0) {
      blockers.push(`${result.breakdown.existingLeadDuplicate} existing lead`);
    }
    if (result.breakdown.duplicateEmailCollapsed > 0) {
      blockers.push(`${result.breakdown.duplicateEmailCollapsed} same-email locs`);
    }
    if (result.breakdown.safetyBlocked > 0) blockers.push(`${result.breakdown.safetyBlocked} hard-blocked`);
    if (result.breakdown.repliedOrOptedOut > 0) {
      blockers.push(`${result.breakdown.repliedOrOptedOut} replied/opted out`);
    }
    if (blockers.length > 0) {
      parts.push(`${result.shortfall} short — ${blockers.slice(0, 3).join(', ')}`);
    } else {
      parts.push(`${result.shortfall} short of target`);
    }
  }
  return parts.join(' · ');
}

export function scoreLeadForWeekQueue(
  lead: WeekQueueRankingLead,
  events: LeadOutreachEvent[] = [],
  vaultMatch?: WeekQueueVaultMatch | null,
  nurtureEmails: Set<string> = new Set(),
  ownerOperatorIds: Set<string> = new Set()
): WeekQueueRankedLead | null {
  if (!isEligibleForWeekQueue(lead, events, vaultMatch, nurtureEmails, ownerOperatorIds)) return null;

  const reasons: string[] = [];
  let score = 0;
  const recordNamed = getLeadContactTier(lead) === 'named';
  const contactSource: WeekQueueRankedLead['contactSource'] =
    recordNamed ? 'record' : vaultMatch ? 'vault' : 'record';

  const composite = lead.lead_score ?? 0;
  score += composite * 0.45;
  if (composite > 0) reasons.push(`score ${composite}`);

  const need = lead.need_score ?? 0;
  score += need * 0.35;
  if (need >= 20) reasons.push('strong need');

  const win = lead.winnability_score ?? 0;
  score += win * 0.15;
  if (win >= 18) reasons.push('winnable');

  const value = lead.value_score ?? 0;
  score += value * 0.05;

  const band = lead.priority_band ?? 'hold';
  score += bandBonus[band];

  const contactTier = getEffectiveContactTier(lead, vaultMatch);
  score += contactTierBonus[contactTier];
  if (contactTier === 'named') {
    reasons.push(contactSource === 'vault' ? 'vault named contact' : 'named contact');
  }

  const painCount = lead.solvable_pain_points?.length ?? 0;
  score += Math.min(painCount * 4, 16);
  if (painCount > 0) reasons.push(`${painCount} pain${painCount === 1 ? '' : 's'}`);

  const proofCount = lead.matched_proof_metric_ids?.length ?? 0;
  score += Math.min(proofCount * 3, 9);
  if (proofCount > 0) reasons.push('proof match');

  const safety = getOutreachSafety(lead);
  if (safety.state === 'ready') {
    score += 8;
    reasons.push('safety clear');
  } else if (safety.state === 'needs_review') {
    score += 2;
  }

  if (contactSource === 'vault') {
    score += 3;
  }

  return {
    leadId: lead.id,
    score: Math.round(score * 10) / 10,
    reasons,
    contactSource,
  };
}

/**
 * One cold send per inbox: keep the highest-scoring lead for each normalized email.
 * Prevents multi-location brands (Native Sun, Wallflower, etc.) from flooding YAMM
 * with the same contact once per door.
 */
export function collapseRankedLeadsByEmail(
  ranked: WeekQueueRankedLead[],
  emailByLeadId: Map<string, string | null | undefined>
): { kept: WeekQueueRankedLead[]; collapsed: number } {
  const bestByEmail = new Map<string, WeekQueueRankedLead>();
  const noEmail: WeekQueueRankedLead[] = [];

  for (const row of ranked) {
    const email = normalizeEmail(emailByLeadId.get(row.leadId));
    if (!email) {
      noEmail.push(row);
      continue;
    }
    const existing = bestByEmail.get(email);
    if (
      !existing ||
      row.score > existing.score ||
      (row.score === existing.score && row.leadId.localeCompare(existing.leadId) < 0)
    ) {
      bestByEmail.set(email, row);
    }
  }

  const kept = [...bestByEmail.values(), ...noEmail].sort(
    (left, right) => right.score - left.score || left.leadId.localeCompare(right.leadId)
  );
  return {
    kept,
    collapsed: Math.max(0, ranked.length - kept.length),
  };
}

export function rankLeadsForWeekQueue(
  leads: WeekQueueRankingLead[],
  eventsByLeadId: Map<string, LeadOutreachEvent[]>,
  limit = WEEK_QUEUE_TARGET_SIZE,
  vaultMatchesByLeadId: Map<string, WeekQueueVaultMatch | null> = new Map(),
  nurtureEmails: Set<string> = new Set(),
  ownerOperatorIds: Set<string> = new Set()
): WeekQueueRankResult {
  const breakdown = summarizeWeekQueueEligibility(
    leads,
    eventsByLeadId,
    vaultMatchesByLeadId,
    nurtureEmails,
    ownerOperatorIds
  );
  const scored = leads
    .map((lead) =>
      scoreLeadForWeekQueue(
        lead,
        eventsByLeadId.get(lead.id) ?? [],
        vaultMatchesByLeadId.get(lead.id) ?? null,
        nurtureEmails,
        ownerOperatorIds
      )
    )
    .filter((row): row is WeekQueueRankedLead => row !== null)
    .sort((left, right) => right.score - left.score || left.leadId.localeCompare(right.leadId));

  const emailByLeadId = new Map<string, string | null | undefined>();
  for (const lead of leads) {
    const vaultMatch = vaultMatchesByLeadId.get(lead.id) ?? null;
    emailByLeadId.set(lead.id, weekQueueLeadEmail(lead, vaultMatch));
  }

  const { kept, collapsed } = collapseRankedLeadsByEmail(scored, emailByLeadId);
  breakdown.duplicateEmailCollapsed = collapsed;
  const ranked = kept.slice(0, limit);

  return {
    ranked,
    breakdown,
    shortfall: Math.max(0, limit - ranked.length),
  };
}
